import React, { useState, useEffect } from 'react';
import { Button, Alert } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useExchangeRate } from '../ExchangeRateContext';
import Loader from '../components/Loader';
import { useAuth } from '../contexts/AuthContext';
import fetchJson from '../hooks/fetchJson';
import Pagination from '../components/Pagination';

const Customer3 = () => {
  const [allCustomer3s, setAllCustomer3s] = useState([]);
  const [selectedCustomer3, setSelectedCustomer3] = useState(null);
  const [sellBatches, setSellBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [debts, setDebts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [totalDebt, setTotalDebt] = useState(0);
  const [totalPayments, setTotalPayments] = useState(0);
  const [remainingDebt, setRemainingDebt] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [expandedSales, setExpandedSales] = useState(new Set());
  const [deliveryPrices, setDeliveryPrices] = useState({});
  const exchangeRate = useExchangeRate();
  const [balance, setBalance] = useState(0);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const { user, token } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [serverTotalItems, setServerTotalItems] = useState(0);
  const [serverHistory, setServerHistory] = useState([]);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [allC3Balances, setAllC3Balances] = useState([]);
  const [loadingAllBalances, setLoadingAllBalances] = useState(false);
  const [c3CurrencyTotals, setC3CurrencyTotals] = useState({});
  const [saleDetails, setSaleDetails] = useState({}); // Cache for sale details by batch_id
  const [loadingSaleDetails, setLoadingSaleDetails] = useState(new Set()); // Track which sales are loading

  const tenantHeaders = () => ({
    ...(user?.ownerId || user?.id ? { 'X-Owner-Id': String(user.ownerId || user.id) } : {}),
    ...(user?.staffId ? { 'X-Staff-Id': String(user.staffId) } : {}),
  });

  // Fetch all customer3s on mount
  useEffect(() => {
    let isMounted = true;
    (async () => {
      const { data, error } = await fetchJson('/api/customer3', { headers: tenantHeaders() });
      if (!isMounted) return;
      if (error) {
        setError(typeof error === 'string' ? error : 'Mijozlarni olishda xatolik');
        setAllCustomer3s([]);
        return;
      }
      const list = Array.isArray(data) ? data : [];
      setAllCustomer3s(list);
    })();
    return () => { isMounted = false; };
  }, []);

  // Fetch unified history (server-side) and balance when customer3 is selected
  useEffect(() => {
    if (!selectedCustomer3) {
      setSellBatches([]);
      setDebts([]);
      setPayments([]);
      setTotalDebt(0);
      setTotalPayments(0);
      setRemainingDebt(0);
      setBalance(0);
      setLoadingBalance(false);
      setServerHistory([]);
      setServerTotalItems(0);
      setCurrentPage(1);
      return;
    }

    setLoading(true);
    setError('');
    setLoadingBalance(true);
    setCurrentPage(1);

    (async () => {
      // Fetch backend-calculated balance
      try {
        const { data: balData } = await fetchJson(`/api/statistics/customer3/${selectedCustomer3.id}/balance`, { headers: tenantHeaders() });
        setBalance(Number(balData?.balance || 0));
      } finally {
        setLoadingBalance(false);
      }

      // Optionally refresh debts/payments aggregates (not shown but kept for consistency)
      const [{ data: debtData }, { data: paymentData }] = await Promise.all([
        fetchJson(`/api/customer3-debts/customer3/${selectedCustomer3.id}`, { headers: tenantHeaders() }),
        fetchJson(`/api/customer3-payments/customer3/${selectedCustomer3.id}`, { headers: tenantHeaders() })
      ]);
      setDebts(debtData?.debts || []);
      setPayments(paymentData?.payments || []);
      setTotalDebt(debtData?.totalDebt || 0);
      setTotalPayments(paymentData?.totalPayments || 0);
      setRemainingDebt(debtData?.remainingDebt || 0);

      // Unified history (server-side pagination)
      const { data: histData } = await fetchJson(`/api/customer3-history/${selectedCustomer3.id}?page=1&limit=${itemsPerPage}`, { headers: tenantHeaders() });
      setServerHistory(Array.isArray(histData?.items) ? histData.items : []);
      setServerTotalItems(Number(histData?.total || 0));

    })()
      .catch(err => {
        setError(err.message || 'Xatolik yuz berdi');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedCustomer3, itemsPerPage]);

  // Fetch unified history when page or items change
  useEffect(() => {
    if (!selectedCustomer3) return;
    setLoading(true);
    (async () => {
      const { data: histData } = await fetchJson(`/api/customer3-history/${selectedCustomer3.id}?page=${currentPage}&limit=${itemsPerPage}`, { headers: tenantHeaders() });
      setServerHistory(Array.isArray(histData?.items) ? histData.items : []);
      setServerTotalItems(Number(histData?.total || 0));
    })()
      .catch(() => {
        setServerHistory([]);
        setServerTotalItems(0);
      })
      .finally(() => setLoading(false));
  }, [selectedCustomer3, currentPage, itemsPerPage]);

  // Fetch delivery prices for sell items in current page history
  useEffect(() => {
    if (!serverHistory || serverHistory.length === 0) return;
    const sells = serverHistory.filter(it => it.type === 'sell' && it.batch_id);
    if (sells.length === 0) return;
    const fetchAll = async () => {
      const prices = {};
      await Promise.all(sells.map(async it => {
        const batchId = it.batch_id;
        const { data } = await fetchJson(`/api/sales/delivery/${batchId}`, { headers: tenantHeaders() });
        prices[batchId] = data?.delivery_price || 0;
      }));
      setDeliveryPrices(prices);
    };
    fetchAll();
  }, [serverHistory]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('uz-UZ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper to get display price for an item based on customer currency
  const getDisplayPrice = (item) => {
    if (selectedCustomer3 && selectedCustomer3.currency === 'USD' && item.price_usd !== undefined) {
      return item.price_usd;
    }
    if (!selectedCustomer3 || !selectedCustomer3.currency) return item.price;
    const customerCurrency = selectedCustomer3.currency;
    const batchCurrency = item.currency || 'UZS';
    if (customerCurrency === batchCurrency) return item.price;
    if (customerCurrency === 'USD' && batchCurrency === 'UZS') {
      return +(item.price / exchangeRate).toFixed(2);
    }
    if (customerCurrency === 'UZS' && batchCurrency === 'USD') {
      return Math.round(item.price * exchangeRate);
    }
    return item.price;
  };

  // Helper to get display currency label
  const getDisplayCurrency = () => {
    if (!selectedCustomer3 || !selectedCustomer3.currency) return 'UZS';
    return selectedCustomer3.currency;
  };

  // Update formatCurrency to use correct currency
  const formatCurrency = (amount, currency) => {
    const displayCurrency = currency || getDisplayCurrency();
    if (displayCurrency === 'USD') {
      return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
    }
    return `${Number(amount).toLocaleString()} UZS`;
  };

  // Compact formatter for button/modal without leading symbol, absolute value
  const formatButtonAmount = (amount, currency) => {
    const displayCurrency = currency || getDisplayCurrency();
    const val = Math.abs(Number(amount) || 0);
    if (displayCurrency === 'USD') {
      return `${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
    }
    return `${val.toLocaleString()} UZS`;
  };

  // Fetch all customer3 balances (for top button + modal)
  const fetchAllC3Balances = async () => {
    setLoadingAllBalances(true);
    try {
      const { data, error } = await fetchJson('/api/statistics/customer3/balances', { headers: tenantHeaders() });
      if (error) {
        setAllC3Balances([]);
        setC3CurrencyTotals({});
        return;
      }
      const items = Array.isArray(data?.items) ? data.items : [];
      setAllC3Balances(items);
      const totals = items.reduce((acc, it) => {
        const curr = (it.currency || 'UZS').toUpperCase();
        acc[curr] = (acc[curr] || 0) + Number(it.balance || 0);
        return acc;
      }, {});
      setC3CurrencyTotals(totals);
    } finally {
      setLoadingAllBalances(false);
    }
  };

  useEffect(() => {
    fetchAllC3Balances();
    // eslint-disable-next-line
  }, []);

  const handlePayment = async () => {
    if (!paymentAmount || paymentAmount <= 0) {
      setError('To\'lov miqdorini kiriting');
      return;
    }
    setLoadingPayment(true);
    try {
      const { error: postErr } = await fetchJson('/api/customer3-payments', {
        method: 'POST',
        headers: tenantHeaders(),
        body: JSON.stringify({
          customer_id: selectedCustomer3.id,
          customer_type: 'customer3',
          amount: Number(paymentAmount),
          payment_date: new Date().toISOString()
        })
      });
      if (postErr) throw new Error('To\'lov qo\'shishda xatolik');
      setPaymentAmount('');
      // Refresh debts and payments
      const [{ data: debtData }, { data: paymentData }] = await Promise.all([
        fetchJson(`/api/customer3-debts/customer3/${selectedCustomer3.id}`, { headers: tenantHeaders() }),
        fetchJson(`/api/customer3-payments/customer3/${selectedCustomer3.id}`, { headers: tenantHeaders() })
      ]);
      setDebts(debtData?.debts || []);
      setPayments(paymentData?.payments || []);
      setTotalDebt(debtData?.totalDebt || 0);
      setTotalPayments(paymentData?.totalPayments || 0);
      setRemainingDebt(debtData?.remainingDebt || 0);
      // Refresh backend-calculated balance
      try {
        setLoadingBalance(true);
        const { data: balData } = await fetchJson(`/api/statistics/customer3/${selectedCustomer3.id}/balance`, { headers: tenantHeaders() });
        setBalance(Number(balData?.balance || 0));
      } finally {
        setLoadingBalance(false);
      }
      // Refresh all-customer balances
      fetchAllC3Balances();
    } catch (err) {
      setError(err.message || 'Xatolik yuz berdi');
    } finally {
      setLoadingPayment(false);
    }
  };

  // Fetch sale details for a specific batch
  const fetchSaleDetails = async (batchId) => {
    if (saleDetails[batchId]) {
      return; // Already cached
    }
    
    setLoadingSaleDetails(prev => new Set([...prev, batchId]));
    try {
      const { data, error } = await fetchJson(`/api/sale-history/by-sellbatch/${batchId}`, { headers: tenantHeaders() });
      if (error) {
        console.error('Failed to fetch sale details for batch:', batchId);
        setSaleDetails(prev => ({
          ...prev,
          [batchId]: []
        }));
      } else {
        setSaleDetails(prev => ({
          ...prev,
          [batchId]: Array.isArray(data) ? data : []
        }));
      }
    } catch (error) {
      console.error('Error fetching sale details:', error);
      setSaleDetails(prev => ({
        ...prev,
        [batchId]: []
      }));
    } finally {
      setLoadingSaleDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(batchId);
        return newSet;
      });
    }
  };

  const toggleSaleDetails = (batchId) => {
    const newExpanded = new Set(expandedSales);
    if (newExpanded.has(batchId)) {
      newExpanded.delete(batchId);
    } else {
      newExpanded.add(batchId);
      // Fetch sale details when expanding
      fetchSaleDetails(batchId);
    }
    setExpandedSales(newExpanded);
  };

  // Balance is fetched from backend (statistics endpoint)

  // Combine server-side history (sells, refunds, payments) for UI
  const getChronologicalHistory = () => {
    return (serverHistory || []).map(it => ({
      type: it.type,
      date: it.date,
      amount: Number(it.amount) || 0,
      batchId: it.batch_id || null,
      paymentId: it.payment_id || null,
      doerName: it.doer_name || 'EGA',
      items: it.type === 'sell' ? (it.items || []) : (it.type === 'refund' ? [{ productName: '', quantity: 0, sellingPrice: 0, currency: '' }] : [])
    }));
  };

  // Expandable sale details component
  const SaleDetails = ({ sale }) => {
    const customerCurrency = getDisplayCurrency();
    const batchId = sale.batchId;
    const items = saleDetails[batchId] || [];
    const isLoading = loadingSaleDetails.has(batchId);
    
    if (isLoading) {
      return (
        <div className="mt-2 p-2 bg-light text-center">
          <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
          <small className="text-muted">Mahsulot tafsilotlari yuklanmoqda...</small>
        </div>
      );
    }
    
    if (!items || !Array.isArray(items) || items.length === 0) {
      return (
        <div className="mt-2 p-2 bg-light">
          <small className="text-muted">Mahsulot tafsilotlari mavjud emas</small>
        </div>
      );
    }
    
    return (
      <div className="mt-2">
        <div className="table-responsive">
          <table className="table table-sm table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>Mahsulot</th>
                <th>Narx</th>
                <th>Miqdor</th>
                <th>Summa</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                // Map backend fields to frontend fields
                let price = item.SalePricePerUnit || item.salepriceperunit || item.sellingPrice || 0;
                let currency = item.Currency || item.currency || 'UZS';
                let quantity = item.QuantitySold || item.quantitysold || item.quantity || 0;
                let productName = item.product_name || item.productName || 'Noma\'lum mahsulot';
                if (customerCurrency === 'USD' && item.price_usd !== undefined) {
                  price = item.price_usd;
                  currency = 'USD';
                } else if (customerCurrency === 'USD' && currency === 'UZS') {
                  price = +(price / exchangeRate).toFixed(2);
                  currency = 'USD';
                } else if (customerCurrency === 'UZS' && currency === 'USD') {
                  price = Math.round(price * exchangeRate);
                  currency = 'UZS';
                }
                return (
                  <tr key={idx}>
                    <td>
                      {productName}
                      {item.barcode && (
                        <small className="d-block text-muted">
                          Barcode: {item.barcode}
                        </small>
                      )}
                    </td>
                    <td>{formatCurrency(price, currency)}</td>
                    <td>{quantity}</td>
                    <td>{formatCurrency(quantity * price, currency)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-end mt-2">
          <strong>Jami: {formatCurrency(items.reduce((sum, item) => {
            // Map backend fields to frontend fields
            let price = item.SalePricePerUnit || item.salepriceperunit || item.sellingPrice || 0;
            let currency = item.Currency || item.currency || 'UZS';
            let quantity = item.QuantitySold || item.quantitysold || item.quantity || 0;
            
            if (customerCurrency === 'USD' && item.price_usd !== undefined) {
              price = item.price_usd;
            } else if (customerCurrency === 'USD' && currency === 'UZS') {
              price = +(price / exchangeRate).toFixed(2);
            } else if (customerCurrency === 'UZS' && currency === 'USD') {
              price = Math.round(price * exchangeRate);
            }
            return sum + (quantity * price);
          }, 0), customerCurrency)}</strong>
        </div>
      </div>
    );
  };

  return (
  <div className="container py-4">
    {/* Top balance button (similar to Mydebt) */}
    <div className='mb-3'>
      <button
        className={`btn ${Object.values(c3CurrencyTotals || {}).some(v => v < 0) ? 'btn-outline-danger' : 'btn-outline-success'}`}
        onClick={() => setShowBalanceModal(true)}
        disabled={loadingAllBalances}
      >
        {loadingAllBalances ? (
          <Loader size={18} />
        ) : (
          (() => {
            const totals = c3CurrencyTotals || {};
            const parts = [];
            if (typeof totals['USD'] !== 'undefined') {
              const usd = totals['USD'];
              parts.push(<span key="usd" className={usd >= 0 ? 'text-success' : 'text-danger'}>{formatButtonAmount(usd, 'USD')}</span>);
            }
            if (typeof totals['UZS'] !== 'undefined') {
              const uzs = totals['UZS'];
              parts.push(<span key="uzs" className={uzs >= 0 ? 'text-success' : 'text-danger'}>{formatButtonAmount(uzs, 'UZS')}</span>);
            }
            const other = Object.keys(totals).filter(k => k !== 'USD' && k !== 'UZS');
            other.forEach(k => {
              const val = totals[k];
              parts.push(<span key={k} className={val >= 0 ? 'text-success' : 'text-danger'}>{formatButtonAmount(val, k)}</span>);
            });
            return <>Balance: {parts.length ? parts.reduce((prev, curr, idx) => [prev, idx ? ' | ' : '', curr]) : '0'}</>;
          })()
        )}
      </button>
    </div>
    {/* Removed aggregate balances UI: rely on per-customer backend balance */}
    <div className="mb-3">
      <div className="row align-items-center">
        <div className="col-md-6">
          <label className="form-label">Mijozni tanlang</label>
          <select
            className="form-select"
            value={selectedCustomer3 ? selectedCustomer3.id : ''}
            onChange={e => {
              const customer = allCustomer3s.find(c => c.id === Number(e.target.value));
              setSelectedCustomer3(customer);
            }}
          >
            <option value="">Tanlang...</option>
            {allCustomer3s.map(c => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.phone})
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6 text-end">
          {selectedCustomer3 && !loading && (
            <div>
              <h6>Balans</h6>
              {loadingBalance ? (
                <Loader size={18} />
              ) : (
                <h4 className={balance >= 0 ? 'text-success' : 'text-danger'}>
                  {formatCurrency(Math.abs(balance), getDisplayCurrency())} {balance >= 0 ? '(Kredit)' : '(Qarz)'}
                </h4>
              )}
            </div>
          )}
        </div>
      </div>
    </div>

    {selectedCustomer3 && (
      <div className="mb-4">
        <h6 className="border-bottom pb-2">To'lov qo'shish</h6>
        <div className="row">
          <div className="col-md-6">
            <input
              type="number"
              className="form-control"
              placeholder="To'lov miqdori..."
              value={paymentAmount}
              onChange={e => setPaymentAmount(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            {/* 'Joriy sotuv narxi' button intentionally omitted */}
          </div>
          <div className="col-md-3">
            <button
              className="btn btn-success w-100"
              onClick={handlePayment}
              disabled={loadingPayment || !paymentAmount}
            >
              {loadingPayment ? 'Qo\'shilmoqda...' : 'To\'lov qo\'shish'}
            </button>
          </div>
        </div>
      </div>
    )}

    {loading && (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Yuklanmoqda...</span>
        </div>
        <div className="mt-2">Yuklanmoqda...</div>
      </div>
    )}

    {/* Chronological History with refunds and pagination */}
    {!loading && !error && selectedCustomer3 && (
      <div className="mb-4">
        <h6 className="border-bottom pb-2">Sotuv, qaytarish va to'lovlar tarixi</h6>
        {(() => {
          const pageItems = getChronologicalHistory();
          const customerCurrency = getDisplayCurrency();
          if (serverTotalItems === 0) {
            return <p className="text-muted">Tarix yo'q</p>;
          }
          return (
            <>
              <div className="border rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {pageItems.map((item, idx) => (
                  <div key={`${item.type}-${idx}-${item.date}`} className={`card mb-2 ${item.type === 'payment' ? 'border-success' : (item.type === 'refund' ? 'border-danger' : 'border-warning')}`}>
                    <div className="card-body p-2">
                      <div className="row align-items-center">
                        <div className="col-md-2">
                          <small className="text-muted">{formatDate(item.date)}</small>
                        </div>
                        <div className="col-md-2">
                          <span className={`badge ${item.type === 'payment' ? 'bg-success' : (item.type === 'refund' ? 'bg-danger' : 'bg-warning')}`}>
                            {item.type === 'payment' ? 'To\'lov' : (item.type === 'refund' ? 'Qaytarish' : 'Sotuv')}
                          </span>
                        </div>
                        <div className="col-md-4">
                          {item.type === 'payment' 
                            ? (
                              <div>
                                <div>{`To'lov #${item.paymentId}`}</div>
                                <small className="text-muted d-block">Bajaruvchi: {item.doerName || 'EGA'}</small>
                              </div>
                            )
                            : item.type === 'refund' ? (
                              <div>
                                <div>Qaytarish #{item.batchId}</div>
                                <small className="text-muted d-block">1 mahsulot</small>
                                <small className="text-muted d-block">Bajaruvchi: {item.doerName || 'EGA'}</small>
                              </div>
                            ) : (
                              <div>
                                <div>Sotuv #{item.batchId}</div>
                                <small className="text-muted d-block">Bajaruvchi: {item.doerName || 'EGA'}</small>
                              </div>
                            )
                          }
                        </div>
                        <div className="col-md-2 text-end">
                          <strong>{formatCurrency(item.amount, customerCurrency)}</strong>
                        </div>
                        <div className="col-md-2 text-end">
                          {(item.type === 'sell' || item.type === 'refund') && (
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => toggleSaleDetails(item.batchId)}
                            >
                              {expandedSales.has(item.batchId) ? 'Yashirish' : 'Batafsil'}
                            </button>
                          )}
                        </div>
                      </div>
                      {/* Show delivery price for sell batches */}
                      {item.type === 'sell' && (
                        <div className="text-end text-primary">
                          {deliveryPrices[item.batchId] > 0 ? (
                            <strong>
                              Jami: {formatCurrency(item.amount, customerCurrency)}
                              {' + '}
                              {formatCurrency(deliveryPrices[item.batchId], customerCurrency)} (Yetkazib berish)
                              {' = '}
                              {formatCurrency(item.amount + deliveryPrices[item.batchId], customerCurrency)}
                            </strong>
                          ) : (
                            <strong>Jami: {formatCurrency(item.amount, customerCurrency)}</strong>
                          )}
                        </div>
                      )}
                      {/* Expandable sale/refund details */}
                      {(item.type === 'sell' || item.type === 'refund') && expandedSales.has(item.batchId) && (
                        <SaleDetails sale={item} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Pagination
                currentPage={currentPage}
                totalItems={serverTotalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
                onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
              />
            </>
          );
        })()}
      </div>
    )}

    {error && (
      <Alert variant="danger" className="mt-3">{error}</Alert>
    )}
    {/* Balance modal for all Customer3 */}
    {showBalanceModal && (
      <div
        className="modal fade show d-block"
        style={{ background: 'rgba(0,0,0,0.3)' }}
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer3BalancesTitle"
        onClick={() => setShowBalanceModal(false)}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg" role="document" onClick={e => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="customer3BalancesTitle">Customer3 Balances</h5>
              <button type="button" className="btn-close" onClick={() => setShowBalanceModal(false)}></button>
            </div>
            <div className="modal-body">
              {loadingAllBalances ? (
                <Loader />
              ) : (
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Balance</th>
                      <th>Currency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allC3Balances.map((c, idx) => (
                      <tr key={c.id}>
                        <td>{idx + 1}</td>
                        <td>{c.name}</td>
                        <td className={Number(c.balance) >= 0 ? 'text-success' : 'text-danger'}>
                          {formatButtonAmount(c.balance, c.currency)}
                        </td>
                        <td>{c.currency}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowBalanceModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);
};

export default Customer3;